'use client'

import { Headline } from '@/frontend/components/ui/headline'
import { getMediaMimeType, Media } from '@/frontend/components/ui/media'
import { Media as MediaType, ProjectTeaserComponent } from '@/payload-types'
import { GridColumn, GridRow } from '@/frontend/components/ui/grid'
import { ArrowExternal, ArrowRight, Checkmark } from '@/frontend/components/ui/icons'
import { MouseFollow } from '@/frontend/components/ui/common/mouse-follow'
import { useMediaQuery } from '@/frontend/hooks/useMediaQuery'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import { useInView } from 'motion/react'
import clsx from 'clsx'
import { Button } from '../../ui/button'

interface ProjectTeaserGroupTypes {
  teaserElements: ProjectTeaserComponent['teaserElements']
}

export const ProjectTeaserGroup = ({ teaserElements }: ProjectTeaserGroupTypes) => {
  const mediaQuery = useMediaQuery('(min-width: 1023px)')
  const t = useTranslations('ProjectTeaser')
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.2,
  })

  return (
    <div className={clsx('project-teaser-group', isInView && 'in-view')} ref={containerRef}>
      <GridRow>
        {teaserElements?.map((teaserElement, index) => {
          const mediaGroup = teaserElement?.mediaGroup
          const mediaContent = getMediaMimeType(mediaGroup.media as MediaType)
          const mediaReference = `media-${index}`
          const additionalMediaGroup = teaserElement?.hoverMediaGroup.mediaGroup
          const additionalMediaContent = getMediaMimeType(additionalMediaGroup.media as MediaType)
          const isWebsiteLink = teaserElement?.linkToLiveWebsite
          const websiteLink = isWebsiteLink ?? '/#'
          const websiteLinkIncludesInternal = websiteLink.includes('halftime.space')
          const followText = websiteLinkIncludesInternal ? t('hoverButton.labelInternal') : t('hoverButton.label')
          const mobileText = websiteLinkIncludesInternal ? t('mobileButton.labelInternal') : t('mobileButton.label')

          return (
            <GridColumn key={index} columnSize={{ xs: 12, m: 6, l: 4 }}>
              <div className="project-teaser-element">
                {isWebsiteLink ? <MouseFollow mediaReference={mediaReference} inlineText={followText} /> : null}
                <a title={teaserElement?.heading || 'Link to project'} href={websiteLink} id={mediaReference} className="media-block" {...(!websiteLinkIncludesInternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                  <Media mediaGroup={mediaGroup} type={mediaContent} sizeLayout="card" lazy={true} />
                  {additionalMediaGroup && mediaQuery && (
                    <div className="additional-image">{additionalMediaGroup && mediaQuery && <Media id={`hover-${mediaReference}`} mediaGroup={additionalMediaGroup} type={additionalMediaContent} lazy={true} />}</div>
                  )}
                </a>
                <div className="teaser-content">
                  {teaserElement?.heading && <Headline as="h3">{teaserElement?.heading}</Headline>}
                  {teaserElement?.description && <p>{teaserElement?.description}</p>}
                  {teaserElement?.projectListPoints?.length ? (
                    <ul className="teaser-points">
                      {teaserElement?.projectListPoints.map((point, index) => {
                        const listPoint = point?.point
                        return (
                          <li key={index} className="teaser-point">
                            {listPoint && (
                              <>
                                <Checkmark /> <span>{listPoint}</span>
                              </>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  ) : null}
                  <Button size="small" colorswoosh="outline" buttontext={mobileText} href={websiteLink} {...(!websiteLinkIncludesInternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {mobileText}
                    {websiteLinkIncludesInternal ? <ArrowRight /> : <ArrowExternal />}
                  </Button>
                </div>
              </div>
            </GridColumn>
          )
        })}
      </GridRow>
    </div>
  )
}
